import { showToast } from '../components/Common/Toast';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import ModernPageHeader from '../components/Common/ModernPageHeader';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/env';
import { supabase } from '../utils/supabaseClient';
import { getDisplayName } from '../utils/formatters';
import { getRoleDisplayName } from '../utils/roleUtils';
import { resolveOvertimeRate } from '../utils/employeeCompensation';
// createClient removed — use imported supabase client for proper auth + RLS

import {
  BanknotesIcon,
  UsersIcon,
  CalendarDaysIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  DocumentCheckIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';


import { companyDateKey, addDaysToDateKey, addMonthsToDateKey } from '../utils/dateFormatters';
// Supabase configuration
// SECURITY: Service key removed - use Edge Functions instead

const Labor = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [laborData, setLaborData] = useState([]);
  const [payRates, setPayRates] = useState({});
  const [timesheetData, setTimesheetData] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Time period settings
  const [timePeriod, setTimePeriod] = useState({
    start: getPeriodStart(),
    end: getPeriodEnd()
  });

  // Employee count
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);

  // #6090/#6091: each From/To edit starts a load, and they can finish out of order. Only the
  // newest load may publish, and laborData remembers the period it was loaded FOR, so an
  // export never labels one period's hours with another period's dates.
  const loadSeqRef = useRef(0);
  const [loadedPeriod, setLoadedPeriod] = useState(null);

  useEffect(() => {
    // A cleared date box made this query `date=gte.` and PostgREST answered 400 (board #4062).
    // Wait for a complete range instead of firing a malformed one.
    if (user?.company_id && timePeriod.start && timePeriod.end) {
      loadLaborData();
      loadEmployeeCount();
    }
  }, [user?.company_id, timePeriod]);

  function getPeriodStart() {
    // First of the company's current month.
    return `${companyDateKey().slice(0, 7)}-01`;
  }

  function getPeriodEnd() {
    // Last day of the company's current month.
    return addDaysToDateKey(addMonthsToDateKey(`${companyDateKey().slice(0, 7)}-01`, 1), -1);
  }

  const loadLaborData = async () => {
    const runId = ++loadSeqRef.current;
    const periodForRun = { start: timePeriod.start, end: timePeriod.end };
    try {
      setLoading(true);

      // ✅ INDUSTRY STANDARD: Query employees table with users join
      const { data: employees, error } = await supabase
        .from('employees')
        .select(`
          id,
          employee_number,
          status,
          hire_date,
          job_title,
          department,
          pay_type,
          capacity_hours_per_day,
          first_name,
          last_name,
          user_id,
          employee_compensation (hourly_rate, overtime_rate),
          users!employees_user_id_fkey (
            id,
            first_name,
            last_name,
            email,
            role
          )
        `)
        .eq('company_id', user.company_id)
        .order('last_name');

      if (error) {
        console.error('❌ Error loading employees:', error);
        throw new Error('Failed to load employees');
      }

      // Transform data to match expected format
      const mappedEmployees = (employees || []).map(emp => ({
        id: emp.id,
        employee_number: emp.employee_number,
        full_name: getDisplayName(emp),
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        email: emp.users?.email || '',
        role: emp.users?.role || '',
        status: emp.status || 'active',
        job_title: emp.job_title || '',
        department: emp.department || '',
        pay_type: emp.pay_type || 'hourly',
        hire_date: emp.hire_date,
        hourly_rate: parseFloat(emp.employee_compensation?.hourly_rate) || 0,
        // #4210: only an UNSET overtime_rate (null/undefined/'') means "1.5x". A stored 0.00 is
        // a deliberate 0 and stays 0 — `||` used to silently turn it into 1.5x base.
        overtime_rate: resolveOvertimeRate(emp.employee_compensation?.overtime_rate, emp.employee_compensation?.hourly_rate),
        capacity_hours_per_day: parseFloat(emp.capacity_hours_per_day) || 8
      }));

      // Load current pay rates for all employees
      await loadPayRates(mappedEmployees);

      // Load timesheet data for the period. Use the RETURNED map, not the
      // timesheetData state — setState is async, so the state var is still the
      // previous render's value inside this same run (it was {} on cold load,
      // which is why every employee showed 0.0h until a date change forced a
      // second run that happened to see the prior run's state).
      const freshTimesheets = await loadTimesheetData(mappedEmployees);

      // Combine data for labor calculation
      const laborCalculations = mappedEmployees.map(employee => {
        const baseRate = employee.hourly_rate || 0;
        const overtimeMultiplier = baseRate > 0 ? (employee.overtime_rate / baseRate) : 1.5;

        const rate = payRates[employee.id] || { base_rate: baseRate, overtime_multiplier: overtimeMultiplier };
        const timeData = freshTimesheets[employee.id] || { regular_hours: 0, overtime_hours: 0 };

        const totalHours = timeData.regular_hours + timeData.overtime_hours;
        const laborCost = (timeData.regular_hours * rate.base_rate) + 
                         (timeData.overtime_hours * rate.base_rate * rate.overtime_multiplier);

        return {
          ...employee,
          ...rate,
          ...timeData,
          total_hours: totalHours,
          labor_cost: laborCost
        };
      });

      // #5771/#5772: the table listed every employee row, inactive and pending invites
      // included (33 rows under "Employees: 25"). Show active staff plus anyone else who
      // actually logged time in the period — a former employee's hours still cost money.
      if (runId !== loadSeqRef.current) return; // a newer period's load superseded this one
      setLaborData(laborCalculations.filter(emp => emp.status === 'active' || emp.total_hours > 0));
      setLoadedPeriod(periodForRun);
      
    } catch (error) {
      console.error('Error loading labor data:', error);
      showToast('error', 'Failed to load labor data');
    } finally {
      if (runId === loadSeqRef.current) setLoading(false);
    }
  };

  const loadPayRates = async (employeesData) => {
    try {
      const ratesByEmployee = {};
      employeesData.forEach(emp => {
        const baseRate = emp.hourly_rate || 0;
        const overtimeRate = emp.overtime_rate ?? (baseRate * 1.5);
        ratesByEmployee[emp.id] = {
          base_rate: baseRate,
          overtime_multiplier: baseRate > 0 ? overtimeRate / baseRate : 1.5
        };
      });

      setPayRates(ratesByEmployee);
    } catch (error) {
      console.error('Error loading pay rates:', error);
    }
  };

  const loadTimesheetData = async (employees) => {
    try {
      const employeeIds = employees.map(emp => emp.id);
      
      // Use authenticated Supabase client
      // Use the already-authenticated supabase client (imported at top)
      
      const { data: timesheets, error } = await supabase
        .from('employee_timesheets')
        .select('employee_id,date,regular_hours,overtime_hours,status')
        .eq('company_id', user.company_id)
        .in('employee_id', employeeIds)
        .gte('date', timePeriod.start)
        .lte('date', timePeriod.end);

      if (error) {
        console.error('Error loading timesheet data:', error);
        // A failed fetch must NOT render as 0.0h — a network/token blip is otherwise
        // indistinguishable from "nobody logged hours", which is how this page reported
        // 0.0h on cold load while the DB held 22.5h of approved time.
        showToast('error', 'Could not load time-tracking hours — please retry.');
        return {};
      }

      // #2858: this kept only status === 'approved', but nothing in the app moves a timesheet
      // to approved — Timesheets labels submitted AND approved as "Logged" (LOGGED_STATUSES,
      // board #457) and that is where entries rest. Live Sep 2026: 19 submitted rows, 147
      // regular + 91.5 overtime hours, shown here as 0.0h. Same definition as Timesheets now.
      const LOGGED_STATUSES = ['submitted', 'approved'];
      const loggedTimesheets = (timesheets || []).filter((t) => LOGGED_STATUSES.includes(t.status));

      // #2859: the STORED regular_hours / overtime_hours (what Timesheets shows and exports),
      // not a re-derivation from clock times. The old derivation took the whole clock span as
      // regular time — which already contains the overtime — and then added overtime_hours on
      // top, so every overtime hour was counted twice.
      const hoursByEmployee = {};
      loggedTimesheets.forEach((t) => {
        if (!hoursByEmployee[t.employee_id]) {
          hoursByEmployee[t.employee_id] = { regular_hours: 0, overtime_hours: 0 };
        }
        hoursByEmployee[t.employee_id].regular_hours += Number(t.regular_hours) || 0;
        hoursByEmployee[t.employee_id].overtime_hours += Number(t.overtime_hours) || 0;
      });

      setTimesheetData(hoursByEmployee);
      return hoursByEmployee;
    } catch (error) {
      console.error('Error loading timesheet data:', error);
      // Same reason as the query-error branch above: never let a thrown fetch failure
      // render silently as 0.0h.
      showToast('error', 'Could not load time-tracking hours — please retry.');
      return {};
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatHours = (hours) => {
    return `${(hours || 0).toFixed(1)}h`;
  };

  // Export timesheet data
  // The period the rows on screen were actually loaded for (see loadSeqRef).
  const exportPeriod = loadedPeriod || timePeriod;

  const exportTimesheetCSV = (dataToExport) => {
    const headers = [
      'Employee Name',
      'Employee ID',
      'Base Rate',
      'Overtime Rate',
      'Regular Hours',
      'Overtime Hours',
      'Total Hours',
      'Labor Cost',
      'Period'
    ];

    const csvData = dataToExport.map(employee => {
      const overtimeRate = (employee.base_rate || 0) * (employee.overtime_multiplier ?? 1.5);
      const regularHours = employee.regular_hours || 0;
      const overtimeHours = employee.overtime_hours || 0;
      const totalHours = regularHours + overtimeHours;
      const laborCost = (regularHours * employee.base_rate) + (overtimeHours * overtimeRate);

      return [
        employee.full_name || 'Unknown',
        employee.id,
        `$${(employee.base_rate || 0).toFixed(2)}`,
        `$${overtimeRate.toFixed(2)}`,
        regularHours.toFixed(1),
        overtimeHours.toFixed(1),
        totalHours.toFixed(1),
        `$${laborCost.toFixed(2)}`,
        `${exportPeriod.start} - ${exportPeriod.end}`
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timesheet_${exportPeriod.start}_to_${exportPeriod.end}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportTimesheetJSON = (dataToExport) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      period: {
        start: exportPeriod.start,
        end: exportPeriod.end
      },
      summary: {
        totalEmployees: dataToExport.length,
        totalRegularHours: dataToExport.reduce((sum, emp) => sum + (emp.regular_hours || 0), 0),
        totalOvertimeHours: dataToExport.reduce((sum, emp) => sum + (emp.overtime_hours || 0), 0),
        totalHours: dataToExport.reduce((sum, emp) => sum + (emp.total_hours || 0), 0),
        totalLaborCost: dataToExport.reduce((sum, emp) => sum + (emp.labor_cost || 0), 0)
      },
      employees: dataToExport.map(employee => {
        const overtimeRate = (employee.base_rate || 0) * (employee.overtime_multiplier ?? 1.5);
        const regularHours = employee.regular_hours || 0;
        const overtimeHours = employee.overtime_hours || 0;

        return {
          id: employee.id,
          name: employee.full_name || 'Unknown',
          // #4592: rounded to 2 dp. overtime_multiplier is derived as overtime_rate / hourly_rate,
          // so it arrives as a raw float (1.4999999999999998) and was written out verbatim.
          rates: {
            base: employee.base_rate || 0,
            overtime: Math.round(overtimeRate * 100) / 100,
            overtimeMultiplier: Math.round((employee.overtime_multiplier ?? 1.5) * 100) / 100
          },
          hours: {
            regular: regularHours,
            overtime: overtimeHours,
            total: regularHours + overtimeHours
          },
          laborCost: {
            regular: regularHours * employee.base_rate,
            overtime: overtimeHours * overtimeRate,
            total: (regularHours * employee.base_rate) + (overtimeHours * overtimeRate)
          }
        };
      })
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `timesheet_${exportPeriod.start}_to_${exportPeriod.end}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Timesheet Approval Functions


  const loadEmployeeCount = async () => {
    try {
      const { count, error } = await supabase
        .from('employees')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', user.company_id)
        // "Active Employees" counted every row, inactive and pending invites too: 27 vs 19
        // active on 2026-10-07 (board #4241).
        .eq('status', 'active');

      if (error) {
        console.error('Error loading employee count:', error);
        return;
      }

      setTotalEmployeeCount(count || 0);
    } catch (error) {
      console.error('Error loading employee count:', error);
    }
  };







  const handleExportTimesheet = () => {
    try {
      // #4598: one CSV builder. This used to assemble its own CSV (different header, period
      // text, quoting and filename) from the same laborData the Export Options menu passes to
      // exportTimesheetCSV, so the two buttons downloaded two different files for one table.
      exportTimesheetCSV(laborData);

      showToast('success', 'Time tracking report exported successfully');
    } catch (error) {
      console.error('Error exporting timesheet:', error);
      showToast('error', 'Failed to export time tracking report');
    }
  };

  // Calculate summary statistics
  const totalLaborCost = laborData.reduce((sum, emp) => sum + emp.labor_cost, 0);
  const totalRegularHours = laborData.reduce((sum, emp) => sum + (emp.regular_hours || 0), 0);
  const totalOvertimeHours = laborData.reduce((sum, emp) => sum + (emp.overtime_hours || 0), 0);
  // Per-employee cost sums (each employee's own rate) — NOT one blanket rate applied to everyone
  const totalRegularCost = laborData.reduce((sum, emp) => sum + ((emp.regular_hours || 0) * (emp.base_rate || 0)), 0);
  const totalOvertimeCost = laborData.reduce((sum, emp) => sum + ((emp.overtime_hours || 0) * (emp.base_rate || 0) * (emp.overtime_multiplier ?? 1.5)), 0);
  const totalHours = totalRegularHours + totalOvertimeHours;
  const activeEmployees = totalEmployeeCount;
  // #5772: averaged over the people who logged time, not over the active headcount (which
  // divided everyone's hours by staff who worked none and read 4.1h for a 34h average).
  const employeesWithHours = laborData.filter(emp => (emp.total_hours || 0) > 0).length;
  const averageHoursPerEmployee = employeesWithHours > 0 ? totalHours / employeesWithHours : 0;

  return (
    <div className="space-y-6">
      <ModernPageHeader
        compact
        title="Labor Costs"
        subtitle="Employee pay rates, labor costs, and analytics"
        icon={ClockIcon}
        gradient="blue"
        stats={[
          { label: 'Total Hours', value: formatHours(totalHours) },
          { label: 'Employees', value: totalEmployeeCount }
        ]}
        actions={[
          {
            label: 'Log Time',
            icon: ClockIcon,
            onClick: () => navigate('/timesheets')
          },
          {
            label: 'Export',
            icon: DocumentArrowDownIcon,
            onClick: handleExportTimesheet
          }
        ]}
      />

      {/* Alert */}
      {alert.show && (
        <div className={`p-4 rounded-md ${
          alert.type === 'success' ? 'bg-green-100 text-green-700' :
          alert.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
          alert.type === 'info' ? 'bg-blue-100 text-blue-700' :
          'bg-red-100 text-red-700'
        }`}>
          {alert.message}
        </div>
      )}

          {/* Time Period Selector */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Time Period</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={timePeriod.start}
                max={timePeriod.end || undefined}
                onChange={(e) => setTimePeriod({ ...timePeriod, start: e.target.value })}
                className="px-3 py-1 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={timePeriod.end}
                min={timePeriod.start || undefined}
                onChange={(e) => setTimePeriod({ ...timePeriod, end: e.target.value })}
                className="px-3 py-1 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {/* #2130: min/max stop the pickers, but a typed date can still cross over. */}
            {timePeriod.start && timePeriod.end && timePeriod.start > timePeriod.end && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                The From date is after the To date, so no time can fall in this range.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* #5606: dark variants — these four cards stayed light pastel in Dark mode. */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-slate-800 dark:to-slate-800/80 rounded-xl border border-green-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-300 mb-1">Total Labor Cost</p>
              <p className="text-3xl font-bold text-green-900 dark:text-white">{formatCurrency(totalLaborCost)}</p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded-full">
              <BanknotesIcon className="h-8 w-8 text-green-600 dark:text-green-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-primary-50 dark:from-slate-800 dark:to-slate-800/80 rounded-xl border border-blue-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-1">Active Employees</p>
              <p className="text-3xl font-bold text-blue-900 dark:text-white">{activeEmployees}</p>
              <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                Avg: {formatHours(averageHoursPerEmployee)} per employee with time logged
              </p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/40 p-3 rounded-full">
              <UsersIcon className="h-8 w-8 text-blue-600 dark:text-blue-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-800 dark:to-slate-800/80 rounded-xl border border-amber-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-300 mb-1">Total Hours</p>
              <p className="text-3xl font-bold text-amber-900 dark:text-white">{formatHours(totalHours)}</p>
              <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                {totalHours > 0 ? `${((totalOvertimeHours / totalHours) * 100).toFixed(1)}% overtime` : 'No hours recorded'}
              </p>
            </div>
            <div className="bg-amber-100 dark:bg-amber-900/40 p-3 rounded-full">
              <ClockIcon className="h-8 w-8 text-amber-600 dark:text-amber-300" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/80 rounded-xl border border-purple-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-300 mb-1">Overtime Hours</p>
              <p className="text-3xl font-bold text-purple-900 dark:text-white">{formatHours(totalOvertimeHours)}</p>
              <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                {totalOvertimeHours > 0 ? `${formatCurrency(totalOvertimeCost)} cost` : 'No overtime'}
              </p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/40 p-3 rounded-full">
              <ExclamationTriangleIcon className="h-8 w-8 text-purple-600 dark:text-purple-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Hours Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">Hours Breakdown</h3>
            {totalHours === 0 && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">No hours recorded</p>}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Regular Hours:</span>
              <span className="font-medium text-gray-900">{formatHours(totalRegularHours)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Overtime Hours:</span>
              <span className="font-medium text-gray-900">{formatHours(totalOvertimeHours)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3">
              <span className="text-gray-900 font-medium">Total Hours:</span>
              <span className="font-bold text-gray-900">{formatHours(totalHours)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">Cost Summary</h3>
            {totalLaborCost === 0 && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">No labor cost yet</p>}
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Regular Hours Cost:</span>
              <span className="font-medium text-gray-900">{formatCurrency(totalRegularCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Overtime Hours Cost:</span>
              <span className="font-medium text-gray-900">{formatCurrency(totalOvertimeCost)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3">
              <span className="text-gray-900 font-medium">Total Labor Cost:</span>
              <span className="font-bold text-gray-900">{formatCurrency(totalLaborCost)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timesheet Export Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/40 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <DocumentArrowDownIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">Need payroll?</h4>
            {/* Board #2131: this named QuickBooks, Gusto and ADP as if the page exported to
                them. There is no payroll integration - the Export control below produces a
                CSV or JSON file, which you import into whichever payroll system you use.
                Describe the export that exists rather than three that do not. */}
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              This page tracks what your labour costs. It does not run payroll - no taxes,
              deductions or direct deposits. Use Export below to download the timesheet as CSV
              or JSON and import it into your payroll provider.
            </p>
          </div>
        </div>
      </div>

      {/* Employee Hours Table */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Employee Hours</h3>

          {/* Export Options */}
          <div className="flex items-center gap-2">
            <div className="relative inline-block text-left">
              <select
                onChange={(e) => {
                  const format = e.target.value;
                  if (format === 'csv') {
                    exportTimesheetCSV(laborData);
                  } else if (format === 'json') {
                    exportTimesheetJSON(laborData);
                  }
                  e.target.value = '';
                }}
                className="btn-secondary flex items-center gap-2 pr-8"
                disabled={loading}
                defaultValue=""
              >
                <option value="" disabled>Export Options</option>
                <option value="csv">📊 Export CSV</option>
                <option value="json">📋 Export JSON</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Base Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overtime Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Regular Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overtime Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Labor Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    Loading labor data...
                  </td>
                </tr>
              ) : laborData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    No labor data found
                  </td>
                </tr>
              ) : (
                laborData.map((employee) => {
                  const overtimeRate = (employee.base_rate || 0) * (employee.overtime_multiplier ?? 1.5);
                  const totalEmpHours = (employee.regular_hours || 0) + (employee.overtime_hours || 0);
                  const laborCost = (employee.regular_hours || 0) * employee.base_rate + (employee.overtime_hours || 0) * overtimeRate;

                  return (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-700">
                                {employee.full_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.full_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {employee.role ? getRoleDisplayName(employee.role) : (employee.job_title || 'No login')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.role === 'owner' && !employee.base_rate
                          ? <span className="text-gray-500">&mdash; <span className="text-xs">(owner)</span></span>
                          : `${formatCurrency(employee.base_rate)}/hr`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.role === 'owner' && !overtimeRate
                          ? <span className="text-gray-500">&mdash;</span>
                          : `${formatCurrency(overtimeRate)}/hr`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatHours(employee.regular_hours)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatHours(employee.overtime_hours)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatHours(totalEmpHours)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(laborCost)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Reg: {formatCurrency((employee.regular_hours || 0) * employee.base_rate)} +
                          OT: {formatCurrency((employee.overtime_hours || 0) * overtimeRate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {/* Board #2125: this used to navigate to the bare /employees list,
                            dropping which employee you had just clicked - you landed on 20
                            rows and had to find them again. ?edit= opens that employee, the
                            same convention Quotes/Customers/Expenses use for ?new=. */}
                        <button
                          onClick={() => navigate(`/employees?edit=${employee.id}`)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded"
                          title="Edit employee pay rates"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Labor;
